import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Check, Loader2, Gift } from 'lucide-react';
import PortalLayout from './PortalLayout';
import SocialStudio from '../admin/SocialStudio';
import { useAuth } from '../../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const muted = { color: 'var(--p-muted)' };
const faint = { color: 'var(--p-faint)' };

export default function ClientStudio() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/me/studio-access-status`, { withCredentials: true })
      .then((r) => setStatus(r.data)).catch(() => setStatus(null)).finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/me/request-studio-access`,
        { note: note.slice(0, 500), phone: phone || null }, { withCredentials: true });
      setDone(true);
    } catch (err) {
      alert('Request failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const approved = status?.is_studio_approved || isAdmin;
  const pending = status?.request_status === 'pending' || done;
  const inputStyle = { background: 'var(--p-input)', borderColor: 'var(--p-border)', color: 'var(--p-text)' };

  // Approved members (and admins) drop straight into the Studio tool, in the
  // member portal shell, with the access their plan grants. No interstitial.
  if (!loading && approved) {
    return (
      <PortalLayout active="studio">
        <SocialStudio embedded />
      </PortalLayout>
    );
  }

  return (
    <PortalLayout active="studio">
      <div className="mb-8" data-testid="portal-studio">
        <span className="text-xs tracking-[0.2em] uppercase text-[#7C3AED] font-semibold p-eyebrow">AI Content Engine</span>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Social Media Viral Studio
        </h1>
        <p className="mt-2 max-w-2xl" style={muted}>Generate viral scripts, posts, carousels, threads and done-for-you content plans up to 90 days tailored to your brand.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1D4ED8]" size={28} /></div>
      ) : pending ? (
        <div className="rounded-2xl p-8 text-center border" style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.30)' }} data-testid="portal-studio-pending">
          <span className="w-14 h-14 rounded-full bg-amber-500/12 text-amber-500 flex items-center justify-center mx-auto mb-4"><Sparkles size={26} /></span>
          <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Request submitted</h3>
          <p className="text-sm mt-2 max-w-md mx-auto" style={muted}>David has been notified. You'll get an email the moment you're approved, usually within 24 hours.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="p-card rounded-2xl p-6" data-testid="portal-studio-info">
            <Sparkles size={22} className="text-[#7C3AED] mb-3" />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>What you get</h3>
            <ul className="space-y-2.5">
              {['Viral hooks, posts, carousels & threads', 'Short-form video scripts with b-roll cues', 'Done-for-you content plans up to 90 days', 'Brand-tuned theme banks & reference library'].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm" style={muted}><Check size={15} className="text-emerald-500 mt-0.5 flex-shrink-0" /> {f}</li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t flex items-start gap-2 text-xs" style={{ borderColor: 'var(--p-border)', ...faint }}>
              <Gift size={14} className="text-[#F97316] mt-0.5 flex-shrink-0" />
              Got a shared-plan invite link from David? Open it to claim a ready-made content plan straight into your account.
            </div>
          </div>

          <form onSubmit={submit} className="p-card rounded-2xl p-6" data-testid="portal-studio-request">
            <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Request access</h3>
            <p className="text-xs mb-4" style={faint}>David approves from his end. You'll get an email when you're in.</p>
            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={faint}>Phone (optional)</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 555 0100"
              className="w-full px-3 py-2.5 mb-3 border rounded-lg text-sm focus:border-[#1D4ED8] outline-none" style={inputStyle} data-testid="portal-studio-phone" />
            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={faint}>Why do you want access?</label>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
              placeholder="e.g. I run a coaching business and want to scale my content output..."
              className="w-full px-3 py-2.5 border rounded-lg text-sm focus:border-[#1D4ED8] outline-none resize-y" style={inputStyle} data-testid="portal-studio-note" />
            <button type="submit" disabled={submitting}
              className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-5 py-3 rounded-lg font-medium text-sm transition-colors disabled:opacity-50" data-testid="portal-studio-submit">
              {submitting ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : 'Request access'}
            </button>
          </form>
        </div>
      )}
    </PortalLayout>
  );
}
